import { useEffect, useMemo, useState } from 'react';

/**
 * The default transformer, which does not actually transform
 */
function passObjectWithoutTransformation<P>(response: unknown) {
  return response as P;
}

/**
 * Make a request using fetch(), and return an object with the loading state and response JSON (if
 * available).
 *
 * URL is only the variable suffix, ie. you can omit the host name and "/api/excillence/get-certified"
 * part. Must start with a "/".
 *
 * If the server response itself should always be transformed before consumption elsewhere, the 2nd
 * hook argument is an optional transform function for your convenience.
 */
export default function useWordpress<P>(
  path: string,
  params: Record<string, string> = {},
  transform: (garbage: unknown) => P = passObjectWithoutTransformation
): {
  isLoading: boolean;
  response: P | null;
} {
  const [response, setResponse] = useState<P | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const url = useMemo(() => {
    let url = process.env.REACT_APP_API_WORDPRESS_URL + path;
    if (Object.keys(params).length) {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        searchParams.set(key, params[key]);
      });
      url += '?' + searchParams.toString();
    }
    return url;
  }, [path, params]);

  useEffect(() => {
    setIsLoading(true);
    fetch(url)
      .then((response) => response.json())
      .then((garbage: unknown) => {
        setIsLoading(false);
        setResponse(transform(garbage));
      });
  }, [url, transform]);

  return {
    isLoading,
    response,
  };
}
